import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import { api } from '@/app/api';

const initialState = {
  profile: null,
  status: 'idle', // idle | loading | authenticated | error
  error: null,
  /** После первого GET /auth/owner/verify (есть httpOnly cookie — js-cookie её не видит). */
  sessionChecked: false,
};

function toErrorMessage(err, fallback) {
  return err.response?.data?.message ?? fallback;
}

/** Восстановление сессии по httpOnly cookie (без чтения document.cookie). */
export const restoreOwnerSession = createAsyncThunk('ownerAuth/restoreSession', async () => {
  await api.get('/auth/owner/verify');
});

export const loginOwner = createAsyncThunk(
  'ownerAuth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/owner/login', { email, password });
      return data.profile;
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, 'Ошибка входа'));
    }
  },
);

export const registerOwner = createAsyncThunk(
  'ownerAuth/register',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/owner/register', { email, password });
      return data.message ?? 'Регистрация успешна';
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, 'Ошибка регистрации'));
    }
  },
);

export const forgotOwnerPassword = createAsyncThunk(
  'ownerAuth/forgot',
  async ({ email }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/owner/forgot', { email });
      return data.message ?? 'Если email зарегистрирован, письмо отправлено';
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, 'Ошибка запроса'));
    }
  },
);

export const resetOwnerPassword = createAsyncThunk(
  'ownerAuth/resetPassword',
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/owner/reset-password', { token, password });
      return data.message ?? 'Пароль обновлён. Можно войти с новым паролем.';
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, 'Не удалось сменить пароль'));
    }
  },
);

export const logoutOwner = createAsyncThunk('ownerAuth/logout', async () => {
  await api.post('/auth/owner/logout').catch(() => undefined);
  Cookies.remove('owner_token');
});

export const loadOwnerProfile = createAsyncThunk(
  'ownerAuth/loadProfile',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/owner/profile');
      return data.profile;
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, 'Не удалось загрузить профиль'));
    }
  },
);

export const updateOwnerProfile = createAsyncThunk(
  'ownerAuth/updateProfile',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.put('/owner/profile', payload);
      return data.profile;
    } catch (err) {
      return rejectWithValue(toErrorMessage(err, 'Не удалось обновить профиль'));
    }
  },
);

const ownerAuthSlice = createSlice({
  name: 'ownerAuth',
  initialState,
  reducers: {
    resetOwnerAuth: () => initialState,
    clearOwnerError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginOwner.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loginOwner.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.sessionChecked = true;
        state.profile = action.payload;
      })
      .addCase(loginOwner.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      })
      .addCase(restoreOwnerSession.fulfilled, (state) => {
        state.sessionChecked = true;
        // не перебиваем активный логин (гонка restore vs login)
        if (state.status === 'loading') return;
        state.status = 'authenticated';
      })
      .addCase(restoreOwnerSession.rejected, (state) => {
        state.sessionChecked = true;
        if (state.status === 'loading' || state.status === 'authenticated') return;
        state.status = 'idle';
        state.profile = null;
      })
      .addCase(logoutOwner.fulfilled, () => initialState)
      .addCase(loadOwnerProfile.fulfilled, (state, action) => {
        state.status = 'authenticated';
        state.profile = action.payload;
      })
      .addCase(loadOwnerProfile.rejected, (state) => {
        state.status = 'idle';
        state.profile = null;
      })
      .addCase(updateOwnerProfile.fulfilled, (state, action) => {
        state.profile = action.payload;
      })
      .addCase(resetOwnerPassword.pending, (state) => {
        state.error = null;
      })
      .addCase(resetOwnerPassword.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(resetOwnerPassword.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { resetOwnerAuth, clearOwnerError } = ownerAuthSlice.actions;
export default ownerAuthSlice.reducer;
